import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { compress } from "@/components/InspectionPhotos";
import { DateInput } from "@/components/DateInput";
import { OperatorSelect, reloadEmployees } from "@/components/Employees";
import { Camera, FolderOpen, Loader2, Plus, Trash2, Save } from "lucide-react";

const today = () => new Date().toLocaleDateString("sv-SE");
const emptyRow = () => ({ vehicle: "", plate: "", works: "", hours: "", materials: {} });
const MATS = ["base", "trasparente", "pastello", "fondo", "varie"];
export const toNum = (h) => parseFloat(String(h).replace(",", ".")) || 0;

export const SheetEditor = ({ s, i, onChange, onRemove }) => {
  const setRow = (ri, k, v) => onChange({ ...s, rows: s.rows.map((r, j) => (j === ri ? { ...r, [k]: k === "plate" ? v.toUpperCase() : v } : r)) });
  return (
    <div className="rounded-xl border border-white/10 bg-[#0B0C10] p-3 space-y-2" data-testid={`ts-sheet-${i}`}>
      <div className="flex flex-wrap gap-2 items-center">
        <OperatorSelect value={s.operator} onChange={(v) => onChange({ ...s, operator: v })} className="flex-1 min-w-[160px] h-10 rounded-md bg-[#181A22] border border-white/10 px-3 text-sm" testid={`ts-sheet-${i}-operator`} />
        <DateInput value={s.date} onChange={(e) => onChange({ ...s, date: e.target.value })} className="w-40 bg-[#181A22] border-white/10" data-testid={`ts-sheet-${i}-date`} aria-label="Data" />
        {onRemove && <Button size="sm" variant="outline" onClick={onRemove} className="rounded-full border-white/15 bg-transparent" data-testid={`ts-sheet-${i}-remove`} aria-label="Scarta scheda"><Trash2 className="w-4 h-4" /></Button>}
      </div>
      {s.rows.map((r, ri) => (
        <div key={ri} className="grid grid-cols-[1fr_1.2fr_64px_auto] sm:grid-cols-[1.3fr_1fr_2fr_70px_auto] gap-1.5" data-testid={`ts-sheet-${i}-row-${ri}`}>
          <Input value={r.vehicle} onChange={(e) => setRow(ri, "vehicle", e.target.value)} placeholder="Veicolo" className="bg-[#181A22] border-white/10" data-testid={`ts-sheet-${i}-row-${ri}-vehicle`} />
          <Input value={r.plate} onChange={(e) => setRow(ri, "plate", e.target.value)} placeholder="Targa" className="bg-[#181A22] border-white/10 font-mono-tech px-2" data-testid={`ts-sheet-${i}-row-${ri}-plate`} />
          <Input value={r.works} onChange={(e) => setRow(ri, "works", e.target.value)} placeholder="Lavori eseguiti" className="bg-[#181A22] border-white/10 col-span-4 sm:col-span-1 order-last sm:order-none" data-testid={`ts-sheet-${i}-row-${ri}-works`} />
          <Input value={r.hours} onChange={(e) => setRow(ri, "hours", e.target.value)} placeholder="Ore" inputMode="decimal" className="bg-[#181A22] border-white/10 text-right" data-testid={`ts-sheet-${i}-row-${ri}-hours`} />
          <Button size="sm" variant="ghost" onClick={() => onChange({ ...s, rows: s.rows.filter((_, j) => j !== ri) })} aria-label="Togli riga" data-testid={`ts-sheet-${i}-row-${ri}-remove`}><Trash2 className="w-4 h-4" /></Button>
          <div className="col-span-4 sm:col-span-5 grid grid-cols-3 sm:grid-cols-5 gap-1.5 pb-2 border-b border-white/5 order-last">
            {MATS.map((m) => (
              <label key={m} className="grid gap-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{m}
                <Input value={r.materials?.[m] || ""} onChange={(e) => setRow(ri, "materials", { ...(r.materials || {}), [m]: e.target.value })} className="h-8 text-xs normal-case tracking-normal text-white bg-[#181A22] border-white/10" data-testid={`ts-sheet-${i}-row-${ri}-mat-${m}`} />
              </label>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button size="sm" variant="ghost" onClick={() => onChange({ ...s, rows: [...s.rows, emptyRow()] })} disabled={s.rows.length >= 8} data-testid={`ts-sheet-${i}-add-row`}><Plus className="w-4 h-4 mr-1" />Aggiungi veicolo</Button>
        <span className="text-sm font-semibold" data-testid={`ts-sheet-${i}-total`}>Totale: {s.rows.reduce((a, r) => a + toNum(r.hours), 0).toLocaleString("it-IT")} h</span>
      </div>
    </div>
  );
};

export const TimesheetUpload = ({ onSaved }) => {
  const gal = useRef(null);
  const cam = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const read = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", await compress(file, 2560, 0.9));
      const { data } = await api.post("/admin/timesheets/read", fd);
      if (!data.sheets.length && data.skipped) { toast.info(`Tutte le ${data.skipped} righe di questa foto erano già state caricate`); return; }
      const sheets = data.sheets.length ? data.sheets : [{ operator: "", date: "", rows: [] }];
      setRes({ photo_id: data.photo_id, sheets: sheets.map((s) => ({ ...s, date: s.date || today(), rows: s.rows.length ? s.rows.map((r) => ({ ...r, hours: r.hours ? String(r.hours).replace(".", ",") : "" })) : [emptyRow()] })) });
      if (data.skipped) toast.info(`${data.skipped} righe erano già state caricate e non verranno ripetute`);
      if (!data.sheets.length) toast.error("Non sono riuscito a leggere la scheda: compila a mano o riprova con una foto più nitida");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const pick = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) read(f); };

  const save = async () => {
    if (res.sheets.some((s) => !s.operator.trim())) { toast.error("Inserisci il nome dell'operatore in ogni scheda"); return; }
    if (res.sheets.some((s) => !s.rows.some((r) => r.vehicle.trim() || r.plate.trim() || r.works.trim() || toNum(r.hours) || Object.values(r.materials || {}).some((q) => q.trim())))) { toast.error("Ogni scheda deve avere almeno un veicolo"); return; }
    setBusy(true);
    try {
      const left = [...res.sheets];
      while (left.length) {
        const s = left[0];
        await api.post("/admin/timesheets", { operator: s.operator, date: s.date, photo_id: res.photo_id, rows: s.rows.map((r) => ({ ...r, hours: toNum(r.hours) })) });
        reloadEmployees();
        left.shift();
        setRes((p) => ({ ...p, sheets: [...left] }));
      }
      toast.success("Ore salvate nel consuntivo");
      setRes(null); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <input ref={gal} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="ts-upload-input" />
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="ts-camera-input" />
      <Button onClick={() => cam.current?.click()} disabled={busy} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="ts-camera-btn">
        {busy && !res ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}{busy && !res ? "Lettura in corso…" : "Fotografa scheda ore"}
      </Button>
      <Button variant="outline" onClick={() => gal.current?.click()} disabled={busy} className="rounded-full border-white/15 bg-transparent" data-testid="ts-gallery-btn" aria-label="Carica foto dalla galleria">
        <FolderOpen className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Dalla galleria</span>
      </Button>
      <Dialog open={!!res} onOpenChange={(o) => !o && setRes(null)}>
        <DialogContent className="max-w-3xl w-[calc(100%-1rem)] rounded-2xl bg-[#12141A] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="ts-review-dialog">
          <DialogHeader>
            <DialogTitle>Controlla le ore lette dalla foto</DialogTitle>
            <DialogDescription>Correggi eventuali errori di lettura prima di salvare.</DialogDescription>
          </DialogHeader>
          {res && (
            <div className="space-y-3">
              {res.sheets.map((s, i) => (
                <SheetEditor key={i} s={s} i={i} onChange={(ns) => setRes({ ...res, sheets: res.sheets.map((x, j) => (j === i ? ns : x)) })} onRemove={() => setRes({ ...res, sheets: res.sheets.filter((_, j) => j !== i) })} />
              ))}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRes({ ...res, sheets: [...res.sheets, { operator: "", date: today(), rows: [emptyRow()] }] })} className="rounded-full border-white/15 bg-transparent" data-testid="ts-add-sheet"><Plus className="w-4 h-4 mr-1" />Altro operatore</Button>
                <Button onClick={save} disabled={busy || !res.sheets.length} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="ts-save-btn">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Salva ore
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
